import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Snowflake, MapPin, Calendar, ArrowRight } from 'lucide-react';

const UWMadison: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "UW Madison";

    return (
        <div className="min-h-screen bg-white font-sans text-[#c5050c] selection:bg-[#c5050c] selection:text-white">
            {/* Utility Bar */}
            <div className="bg-[#282728] text-white py-2 px-6 flex justify-between items-center text-sm font-bold">
                <div className="flex gap-4">
                     <a href="#" className="hover:underline">My {schoolName.split(' ')[0]}</a>
                     <a href="#" className="hover:underline">Map</a>
                </div>
                 <div className="flex gap-4">
                     <a href="#" className="hover:underline">Directory</a>
                     <Search size={18} className="cursor-pointer"/>
                </div>
            </div>

            {/* Nav */}
            <nav className="bg-[#c5050c] text-white py-6 px-6 md:px-12 shadow-md">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <span className="font-serif text-3xl font-bold tracking-tight">{schoolName.toUpperCase()}</span>
                    </div>

                    <div className="hidden lg:flex gap-6 font-bold text-white text-sm">
                         <a href="#" className="hover:underline opacity-90 hover:opacity-100">Admissions</a>
                         <a href="#" className="hover:underline opacity-90 hover:opacity-100">Academics</a>
                         <a href="#" className="hover:underline opacity-90 hover:opacity-100">Research</a>
                         <a href="#" className="hover:underline opacity-90 hover:opacity-100">Campus Life</a>
                         <a href="#" className="hover:underline opacity-90 hover:opacity-100">About</a>
                    </div>
                     <Menu className="cursor-pointer lg:hidden text-white" size={28} />
                </div>
            </nav>

            {/* Hero - Terrace / Lake */}
            <header className="relative h-[600px]">
                <img 
                    src="https://images.unsplash.com/photo-1585675402097-40dc1d05904d?q=80&w=2070&auto=format&fit=crop" 
                    alt="Memorial Union Terrace" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

                <div className="absolute bottom-0 left-0 w-full p-12 md:p-20">
                     <div className="max-w-3xl mx-auto text-center text-white">
                         <h1 className="text-4xl md:text-6xl font-serif font-bold mb-6 drop-shadow-lg">
                             Experience the Wisconsin Idea.
                         </h1>
                         <p className="text-xl md:text-2xl mb-8 font-light drop-shadow-md">
                             Education should influence people’s lives beyond the boundaries of the classroom.
                         </p>
                         <Button className="bg-[#c5050c] hover:bg-[#9b0000] text-white font-bold px-10 h-14 rounded-sm border-2 border-white">
                             Apply to {schoolName.split(' ')[0]}
                         </Button>
                     </div>
                </div>
            </header>

            {/* News - Card Layout */}
            <section className="bg-[#f5f5f5] py-24 px-6 md:px-12">
                <div className="max-w-[1200px] mx-auto">
                    <h2 className="text-3xl font-bold text-[#c5050c] mb-12 border-b border-gray-300 pb-4">Latest News</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="bg-white shadow-sm border-t-4 border-[#c5050c] p-6 hover:shadow-lg transition-shadow cursor-pointer">
                            <span className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-3 block">Science</span>
                            <h3 className="text-xl font-serif font-bold text-[#282728] mb-4 hover:text-[#c5050c] transition-colors">{schoolName} researchers discover new enzyme</h3>
                            <p className="text-gray-600 text-sm mb-4">Discovery could lead to more efficient biofuels.</p>
                             <div className="text-[#c5050c] font-bold text-sm flex items-center gap-1">Read More <ArrowRight size={14}/></div>
                        </div>
                         <div className="bg-white shadow-sm border-t-4 border-[#c5050c] p-6 hover:shadow-lg transition-shadow cursor-pointer">
                            <span className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-3 block">Campus</span>
                            <h3 className="text-xl font-serif font-bold text-[#282728] mb-4 hover:text-[#c5050c] transition-colors">Union South celebrates 10th anniversary</h3>
                            <p className="text-gray-600 text-sm mb-4">A decade of serving students and the community.</p>
                             <div className="text-[#c5050c] font-bold text-sm flex items-center gap-1">Read More <ArrowRight size={14}/></div>
                        </div>
                         <div className="bg-white shadow-sm border-t-4 border-[#c5050c] p-6 hover:shadow-lg transition-shadow cursor-pointer">
                            <span className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-3 block">Arts</span>
                            <h3 className="text-xl font-serif font-bold text-[#282728] mb-4 hover:text-[#c5050c] transition-colors">Chazen Museum opens new exhibition</h3>
                            <p className="text-gray-600 text-sm mb-4">Featuring works from local artists.</p>
                             <div className="text-[#c5050c] font-bold text-sm flex items-center gap-1">Read More <ArrowRight size={14}/></div>
                        </div>
                    </div>
                </div>
            </section>

             {/* Footer */}
             <footer className="bg-[#282728] text-gray-300 py-16 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 font-sans text-sm">
                     <div className="col-span-1 md:col-span-2">
                         <span className="font-serif font-bold text-2xl text-white mb-6 block">{schoolName}</span>
                         <p className="mb-4">
                             Madison, WI 53706 <br/>
                             (608) 263-2400
                         </p>
                          <div className="flex gap-4 text-white">
                             {/* Social Placeholder */}
                             <div className="w-8 h-8 bg-[#c5050c] rounded-sm"></div>
                             <div className="w-8 h-8 bg-[#c5050c] rounded-sm"></div>
                             <div className="w-8 h-8 bg-[#c5050c] rounded-sm"></div>
                         </div>
                     </div>

                     <div>
                         <h4 className="text-white font-bold uppercase tracking-widest mb-6 text-xs">Resources</h4>
                         <ul className="space-y-3 hover:[&_a]:text-white">
                             <li><a href="#">Map</a></li>
                             <li><a href="#">Directory</a></li>
                             <li><a href="#">Library</a></li>
                             <li><a href="#">MyUW</a></li>
                         </ul>
                     </div>

                      <div>
                         <h4 className="text-white font-bold uppercase tracking-widest mb-6 text-xs">Support</h4>
                         <ul className="space-y-3 hover:[&_a]:text-white">
                             <li><a href="#">Counseling Services</a></li>
                             <li><a href="#">Safety</a></li>
                             <li><a href="#">Accessibility</a></li>
                             <li><a href="#">Contact Us</a></li>
                         </ul>
                     </div>
                 </div>
                 <div className="max-w-[1200px] mx-auto mt-16 text-center text-xs text-gray-500 border-t border-gray-700 pt-8">
                     © 2026 Board of Regents of the {schoolName} System
                 </div>
             </footer>
        </div>
    );
};

export default UWMadison;
